import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Tooltip,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as BankIcon,
  TrendingUp as InvestmentIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
} from '@mui/icons-material';
import { useApp } from '../App';
import { bankAccountsApi, investmentAccountsApi, holdersApi, jurisdictionsApi } from '../services/api';

const BANK_ACCOUNT_TYPES = [
  { value: 'CHECKING', label: 'Расчётный' },
  { value: 'SAVINGS', label: 'Сберегательный' },
  { value: 'DEPOSIT', label: 'Депозитный' },
  { value: 'CURRENT', label: 'Текущий' },
  { value: 'ESCROW', label: 'Эскроу' },
];

const INVESTMENT_ACCOUNT_TYPES = [
  { value: 'BROKERAGE', label: 'Брокерский' },
  { value: 'CUSTODY', label: 'Кастодиальный' },
  { value: 'IRA', label: 'IRA' },
  { value: 'ISA', label: 'ISA' },
  { value: 'PENSION', label: 'Пенсионный' },
  { value: 'TRUST', label: 'Трастовый' },
  { value: 'MARGIN', label: 'Маржинальный' },
];

const MANAGEMENT_TYPES = [
  { value: 'SELF_DIRECTED', label: 'Самостоятельное' },
  { value: 'DISCRETIONARY', label: 'Доверительное' },
  { value: 'ADVISORY', label: 'Консультационное' },
];

const CURRENCIES = ['USD', 'EUR', 'RUB', 'GBP', 'CHF', 'AED', 'CNY'];

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

export default function Accounts() {
  const { showNotification } = useApp();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Bank accounts state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [bankEditItem, setBankEditItem] = useState(null);

  // Investment accounts state
  const [investmentAccounts, setInvestmentAccounts] = useState([]);
  const [investmentDialogOpen, setInvestmentDialogOpen] = useState(false);
  const [investmentEditItem, setInvestmentEditItem] = useState(null);

  // Common data
  const [holders, setHolders] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);

  // Bank account form
  const [bankForm, setBankForm] = useState({
    accountNumber: '',
    bankName: '',
    bankCode: '',
    accountType: 'CURRENT',
    currency: 'USD',
    balance: '',
    interestRate: '',
    openDate: '',
    maturityDate: '',
    iban: '',
    correspondentBank: '',
    correspondentAccount: '',
    purpose: '',
    notes: '',
    holderId: '',
    jurisdictionId: '',
  });

  // Investment account form
  const [investmentForm, setInvestmentForm] = useState({
    accountNumber: '',
    brokerName: '',
    accountType: 'BROKERAGE',
    currency: 'USD',
    cashBalance: '',
    marketValue: '',
    openDate: '',
    custodian: '',
    managementType: 'SELF_DIRECTED',
    feeStructure: '',
    notes: '',
    holderId: '',
    jurisdictionId: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [bankRes, investmentRes, holdersRes, jurisdictionsRes] = await Promise.all([
        bankAccountsApi.getAll({ search }),
        investmentAccountsApi.getAll({ search }),
        holdersApi.getAll({ isActive: true }),
        jurisdictionsApi.getAll({ isActive: true }),
      ]);

      setBankAccounts(bankRes.data);
      setInvestmentAccounts(investmentRes.data);
      setHolders(holdersRes.data);
      setJurisdictions(jurisdictionsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      showNotification('Ошибка загрузки данных', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, showNotification]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Bank account handlers
  const handleBankDialogOpen = (item = null) => {
    if (item) {
      setBankEditItem(item);
      setBankForm({
        accountNumber: item.accountNumber || '',
        bankName: item.bankName || '',
        bankCode: item.bankCode || '',
        accountType: item.accountType || 'CURRENT',
        currency: item.currency || 'USD',
        balance: item.balance || '',
        interestRate: item.interestRate || '',
        openDate: item.openDate || '',
        maturityDate: item.maturityDate || '',
        iban: item.iban || '',
        correspondentBank: item.correspondentBank || '',
        correspondentAccount: item.correspondentAccount || '',
        purpose: item.purpose || '',
        notes: item.notes || '',
        holderId: item.holderId || '',
        jurisdictionId: item.jurisdictionId || '',
      });
    } else {
      setBankEditItem(null);
      setBankForm({
        accountNumber: '',
        bankName: '',
        bankCode: '',
        accountType: 'CURRENT',
        currency: 'USD',
        balance: '',
        interestRate: '',
        openDate: '',
        maturityDate: '',
        iban: '',
        correspondentBank: '',
        correspondentAccount: '',
        purpose: '',
        notes: '',
        holderId: '',
        jurisdictionId: '',
      });
    }
    setBankDialogOpen(true);
  };

  const handleBankSubmit = async () => {
    try {
      if (bankEditItem) {
        await bankAccountsApi.update(bankEditItem.id, bankForm);
        showNotification('Счёт обновлён', 'success');
      } else {
        await bankAccountsApi.create(bankForm);
        showNotification('Счёт создан', 'success');
      }
      setBankDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving bank account:', error);
      showNotification('Ошибка сохранения', 'error');
    }
  };

  const handleBankDelete = async (id) => {
    if (!window.confirm('Удалить этот банковский счёт?')) return;
    try {
      await bankAccountsApi.delete(id);
      showNotification('Счёт удалён', 'success');
      loadData();
    } catch (error) {
      console.error('Error deleting bank account:', error);
      showNotification('Ошибка удаления', 'error');
    }
  };

  const handleBankToggleActive = async (item) => {
    try {
      if (item.isActive) {
        await bankAccountsApi.deactivate(item.id);
        showNotification('Счёт деактивирован', 'info');
      } else {
        await bankAccountsApi.activate(item.id);
        showNotification('Счёт активирован', 'success');
      }
      loadData();
    } catch (error) {
      console.error('Error toggling bank account status:', error);
      showNotification('Ошибка изменения статуса', 'error');
    }
  };

  // Investment account handlers
  const handleInvestmentDialogOpen = (item = null) => {
    if (item) {
      setInvestmentEditItem(item);
      setInvestmentForm({
        accountNumber: item.accountNumber || '',
        brokerName: item.brokerName || '',
        accountType: item.accountType || 'BROKERAGE',
        currency: item.currency || 'USD',
        cashBalance: item.cashBalance || '',
        marketValue: item.marketValue || '',
        openDate: item.openDate || '',
        custodian: item.custodian || '',
        managementType: item.managementType || 'SELF_DIRECTED',
        feeStructure: item.feeStructure || '',
        notes: item.notes || '',
        holderId: item.holderId || '',
        jurisdictionId: item.jurisdictionId || '',
      });
    } else {
      setInvestmentEditItem(null);
      setInvestmentForm({
        accountNumber: '',
        brokerName: '',
        accountType: 'BROKERAGE',
        currency: 'USD',
        cashBalance: '',
        marketValue: '',
        openDate: '',
        custodian: '',
        managementType: 'SELF_DIRECTED',
        feeStructure: '',
        notes: '',
        holderId: '',
        jurisdictionId: '',
      });
    }
    setInvestmentDialogOpen(true);
  };

  const handleInvestmentSubmit = async () => {
    try {
      if (investmentEditItem) {
        await investmentAccountsApi.update(investmentEditItem.id, investmentForm);
        showNotification('Счёт обновлён', 'success');
      } else {
        await investmentAccountsApi.create(investmentForm);
        showNotification('Счёт создан', 'success');
      }
      setInvestmentDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving investment account:', error);
      showNotification('Ошибка сохранения', 'error');
    }
  };

  const handleInvestmentDelete = async (id) => {
    if (!window.confirm('Удалить этот инвестиционный счёт?')) return;
    try {
      await investmentAccountsApi.delete(id);
      showNotification('Счёт удалён', 'success');
      loadData();
    } catch (error) {
      console.error('Error deleting investment account:', error);
      showNotification(error.response?.data?.error || 'Ошибка удаления', 'error');
    }
  };

  const handleInvestmentToggleActive = async (item) => {
    try {
      if (item.isActive) {
        await investmentAccountsApi.deactivate(item.id);
        showNotification('Счёт деактивирован', 'info');
      } else {
        await investmentAccountsApi.activate(item.id);
        showNotification('Счёт активирован', 'success');
      }
      loadData();
    } catch (error) {
      console.error('Error toggling investment account status:', error);
      showNotification('Ошибка изменения статуса', 'error');
    }
  };

  // Calculate totals
  const bankTotals = bankAccounts.reduce((acc, item) => {
    const balance = parseFloat(item.balance) || 0;
    if (!acc[item.currency]) acc[item.currency] = 0;
    acc[item.currency] += balance;
    return acc;
  }, {});

  const investmentTotals = investmentAccounts.reduce((acc, item) => {
    const total = (parseFloat(item.cashBalance) || 0) + (parseFloat(item.marketValue) || 0);
    if (!acc[item.currency]) acc[item.currency] = 0;
    acc[item.currency] += total;
    return acc;
  }, {});

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Справочник счетов
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Поиск..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <IconButton onClick={loadData}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BankIcon />
                <Typography variant="h6">Банковские счета</Typography>
              </Box>
              <Typography variant="h4">{bankAccounts.length}</Typography>
              <Box sx={{ mt: 1 }}>
                {Object.entries(bankTotals).map(([currency, total]) => (
                  <Typography key={currency} variant="body2">
                    {formatCurrency(total, currency)}
                  </Typography>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'secondary.main', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <InvestmentIcon />
                <Typography variant="h6">Инвестиционные счета</Typography>
              </Box>
              <Typography variant="h4">{investmentAccounts.length}</Typography>
              <Box sx={{ mt: 1 }}>
                {Object.entries(investmentTotals).map(([currency, total]) => (
                  <Typography key={currency} variant="body2">
                    {formatCurrency(total, currency)}
                  </Typography>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab icon={<BankIcon />} label="Банковские счета" />
          <Tab icon={<InvestmentIcon />} label="Инвестиционные счета" />
        </Tabs>
      </Paper>

      {/* Bank Accounts Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleBankDialogOpen()}
          >
            Добавить счёт
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Банк</TableCell>
                <TableCell>Номер счёта</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Валюта</TableCell>
                <TableCell align="right">Баланс</TableCell>
                <TableCell>Владелец</TableCell>
                <TableCell>Юрисдикция</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell align="center">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bankAccounts.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{item.bankName}</Typography>
                    {item.bankCode && (
                      <Typography variant="caption" color="text.secondary">{item.bankCode}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{item.accountNumber}</Typography>
                    {item.iban && (
                      <Typography variant="caption" color="text.secondary">{item.iban}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={BANK_ACCOUNT_TYPES.find(t => t.value === item.accountType)?.label || item.accountType}
                    />
                  </TableCell>
                  <TableCell>{item.currency}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(item.balance, item.currency)}
                  </TableCell>
                  <TableCell>{item.Holder?.name || '-'}</TableCell>
                  <TableCell>{item.Jurisdiction?.name || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      icon={item.isActive ? <ActiveIcon /> : <BlockIcon />}
                      label={item.isActive ? 'Активен' : 'Неактивен'}
                      color={item.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Редактировать">
                      <IconButton size="small" onClick={() => handleBankDialogOpen(item)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={item.isActive ? 'Деактивировать' : 'Активировать'}>
                      <IconButton size="small" onClick={() => handleBankToggleActive(item)}>
                        {item.isActive ? <BlockIcon fontSize="small" /> : <ActiveIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Удалить">
                      <IconButton size="small" onClick={() => handleBankDelete(item.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {bankAccounts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      Нет банковских счетов
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Investment Accounts Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleInvestmentDialogOpen()}
          >
            Добавить счёт
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Брокер</TableCell>
                <TableCell>Номер счёта</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Управление</TableCell>
                <TableCell align="right">Денежные средства</TableCell>
                <TableCell align="right">Рыночная стоимость</TableCell>
                <TableCell>Владелец</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell align="center">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {investmentAccounts.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{item.brokerName}</Typography>
                    {item.custodian && (
                      <Typography variant="caption" color="text.secondary">Кастодиан: {item.custodian}</Typography>
                    )}
                  </TableCell>
                  <TableCell>{item.accountNumber}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={INVESTMENT_ACCOUNT_TYPES.find(t => t.value === item.accountType)?.label || item.accountType}
                    />
                  </TableCell>
                  <TableCell>
                    {MANAGEMENT_TYPES.find(t => t.value === item.managementType)?.label || item.managementType}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(item.cashBalance, item.currency)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(item.marketValue, item.currency)}
                  </TableCell>
                  <TableCell>{item.Holder?.name || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      icon={item.isActive ? <ActiveIcon /> : <BlockIcon />}
                      label={item.isActive ? 'Активен' : 'Неактивен'}
                      color={item.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Редактировать">
                      <IconButton size="small" onClick={() => handleInvestmentDialogOpen(item)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={item.isActive ? 'Деактивировать' : 'Активировать'}>
                      <IconButton size="small" onClick={() => handleInvestmentToggleActive(item)}>
                        {item.isActive ? <BlockIcon fontSize="small" /> : <ActiveIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Удалить">
                      <IconButton size="small" onClick={() => handleInvestmentDelete(item.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {investmentAccounts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      Нет инвестиционных счетов
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Bank Account Dialog */}
      <Dialog open={bankDialogOpen} onClose={() => setBankDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {bankEditItem ? 'Редактировать банковский счёт' : 'Добавить банковский счёт'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Название банка"
                value={bankForm.bankName}
                onChange={(e) => setBankForm({ ...bankForm, bankName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SWIFT/BIC код"
                value={bankForm.bankCode}
                onChange={(e) => setBankForm({ ...bankForm, bankCode: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Номер счёта"
                value={bankForm.accountNumber}
                onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="IBAN"
                value={bankForm.iban}
                onChange={(e) => setBankForm({ ...bankForm, iban: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Тип счёта</InputLabel>
                <Select
                  value={bankForm.accountType}
                  label="Тип счёта"
                  onChange={(e) => setBankForm({ ...bankForm, accountType: e.target.value })}
                >
                  {BANK_ACCOUNT_TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Валюта</InputLabel>
                <Select
                  value={bankForm.currency}
                  label="Валюта"
                  onChange={(e) => setBankForm({ ...bankForm, currency: e.target.value })}
                >
                  {CURRENCIES.map(c => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Баланс"
                value={bankForm.balance}
                onChange={(e) => setBankForm({ ...bankForm, balance: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Владелец</InputLabel>
                <Select
                  value={bankForm.holderId}
                  label="Владелец"
                  onChange={(e) => setBankForm({ ...bankForm, holderId: e.target.value })}
                >
                  <MenuItem value="">Не указан</MenuItem>
                  {holders.map(h => (
                    <MenuItem key={h.id} value={h.id}>{h.name} ({h.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Юрисдикция</InputLabel>
                <Select
                  value={bankForm.jurisdictionId}
                  label="Юрисдикция"
                  onChange={(e) => setBankForm({ ...bankForm, jurisdictionId: e.target.value })}
                >
                  <MenuItem value="">Не указана</MenuItem>
                  {jurisdictions.map(j => (
                    <MenuItem key={j.id} value={j.id}>{j.name} ({j.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Дата открытия"
                value={bankForm.openDate}
                onChange={(e) => setBankForm({ ...bankForm, openDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Процентная ставка (%)"
                value={bankForm.interestRate}
                onChange={(e) => setBankForm({ ...bankForm, interestRate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Банк-корреспондент"
                value={bankForm.correspondentBank}
                onChange={(e) => setBankForm({ ...bankForm, correspondentBank: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Корр. счёт"
                value={bankForm.correspondentAccount}
                onChange={(e) => setBankForm({ ...bankForm, correspondentAccount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Назначение"
                value={bankForm.purpose}
                onChange={(e) => setBankForm({ ...bankForm, purpose: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Примечания"
                value={bankForm.notes}
                onChange={(e) => setBankForm({ ...bankForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBankDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleBankSubmit}>
            {bankEditItem ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Investment Account Dialog */}
      <Dialog open={investmentDialogOpen} onClose={() => setInvestmentDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {investmentEditItem ? 'Редактировать инвестиционный счёт' : 'Добавить инвестиционный счёт'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Название брокера"
                value={investmentForm.brokerName}
                onChange={(e) => setInvestmentForm({ ...investmentForm, brokerName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Номер счёта"
                value={investmentForm.accountNumber}
                onChange={(e) => setInvestmentForm({ ...investmentForm, accountNumber: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Тип счёта</InputLabel>
                <Select
                  value={investmentForm.accountType}
                  label="Тип счёта"
                  onChange={(e) => setInvestmentForm({ ...investmentForm, accountType: e.target.value })}
                >
                  {INVESTMENT_ACCOUNT_TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Тип управления</InputLabel>
                <Select
                  value={investmentForm.managementType}
                  label="Тип управления"
                  onChange={(e) => setInvestmentForm({ ...investmentForm, managementType: e.target.value })}
                >
                  {MANAGEMENT_TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Валюта</InputLabel>
                <Select
                  value={investmentForm.currency}
                  label="Валюта"
                  onChange={(e) => setInvestmentForm({ ...investmentForm, currency: e.target.value })}
                >
                  {CURRENCIES.map(c => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Денежные средства"
                value={investmentForm.cashBalance}
                onChange={(e) => setInvestmentForm({ ...investmentForm, cashBalance: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Рыночная стоимость"
                value={investmentForm.marketValue}
                onChange={(e) => setInvestmentForm({ ...investmentForm, marketValue: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Владелец</InputLabel>
                <Select
                  value={investmentForm.holderId}
                  label="Владелец"
                  onChange={(e) => setInvestmentForm({ ...investmentForm, holderId: e.target.value })}
                >
                  <MenuItem value="">Не указан</MenuItem>
                  {holders.map(h => (
                    <MenuItem key={h.id} value={h.id}>{h.name} ({h.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Юрисдикция</InputLabel>
                <Select
                  value={investmentForm.jurisdictionId}
                  label="Юрисдикция"
                  onChange={(e) => setInvestmentForm({ ...investmentForm, jurisdictionId: e.target.value })}
                >
                  <MenuItem value="">Не указана</MenuItem>
                  {jurisdictions.map(j => (
                    <MenuItem key={j.id} value={j.id}>{j.name} ({j.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Кастодиан"
                value={investmentForm.custodian}
                onChange={(e) => setInvestmentForm({ ...investmentForm, custodian: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Дата открытия"
                value={investmentForm.openDate}
                onChange={(e) => setInvestmentForm({ ...investmentForm, openDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Структура комиссий"
                value={investmentForm.feeStructure}
                onChange={(e) => setInvestmentForm({ ...investmentForm, feeStructure: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Примечания"
                value={investmentForm.notes}
                onChange={(e) => setInvestmentForm({ ...investmentForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInvestmentDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleInvestmentSubmit}>
            {investmentEditItem ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
